import { groupBy, orderBy, sortBy } from 'es-toolkit'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router'
import Select, { type MultiValue, type StylesConfig } from 'react-select'
import { useShallow } from 'zustand/react/shallow'

import { Tag } from '@/api/type'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SearchBox } from '@/components/ui/search-box'
import { useAppStore } from '@/store/useAppStore'
import { useMediaStore } from '@/store/useMediaStore'

type OptionType = {
  label: string
  value: number
}

// 読書状態の定義
type ReadingStatus = 'unread' | 'reading' | 'completed'
const ALL_READING_STATUSES: ReadingStatus[] = ['unread', 'reading', 'completed']
const READING_STATUS_LABELS: Record<ReadingStatus, string> = {
  unread: '未読',
  reading: '読書中',
  completed: '読了',
}

// お気に入りフィルターの定義
type FavoriteFilter = 'all' | 'favorite' | 'not_favorite'
const FAVORITE_OPTIONS: { value: FavoriteFilter; label: string }[] = [
  { value: 'all', label: '全て' },
  { value: 'favorite', label: 'お気に入り' },
  { value: 'not_favorite', label: 'お気に入り以外' },
]

type SearchPanelProps = {
  domain?: string
  onClose?: () => void
}

export const SearchPanel = ({ domain, onClose }: SearchPanelProps) => {
  const { getTags, tags, tagsDomain } = useMediaStore(useShallow((state) => state))
  const hasFullAccess = useAppStore((state) => state.hasFullAccess)
  const [params, setParams] = useSearchParams()

  const [searchInput, setSearchInput] = useState(params.get('title') || '')
  const [pageFilter, setPageFilter] = useState<(number | undefined)[]>([
    Number(params.get('min')) || undefined,
    Number(params.get('max')) || undefined,
  ])
  const [tagFilter, setTagFilter] = useState<number[]>(
    params
      .get('tags')
      ?.split(',')
      .map((t) => Number(t)) || [],
  )
  // お気に入りフィルター
  const [favoriteFilter, setFavoriteFilter] = useState<FavoriteFilter>(() => {
    const fav = params.get('favorite')
    if (fav === 'true') return 'favorite'
    if (fav === 'false') return 'not_favorite'
    return 'all'
  })
  // 読書状態フィルター（デフォルトですべて選択）
  const [readingStatusFilter, setReadingStatusFilter] = useState<ReadingStatus[]>(() => {
    const statusParam = params.get('reading_status')
    if (statusParam) {
      return statusParam.split(',') as ReadingStatus[]
    }
    return [...ALL_READING_STATUSES]
  })

  useEffect(() => {
    // 同じドメインのタグがロード済みならスキップ
    if (tagsDomain === domain) return
    if (domain && hasFullAccess) {
      getTags(domain, undefined, false) // ローディング非表示
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domain, hasFullAccess, tagsDomain])

  // 別ドメインのタグが残っている間は表示しない
  const domainTags = useMemo(() => (tagsDomain === domain ? tags : []), [tags, tagsDomain, domain])

  const pageMin = useMemo(() => {
    const min = params.get('min')
    if (min) return Number(min)
    return undefined
  }, [params])

  const pageMax = useMemo(() => {
    const max = params.get('max')
    if (max) return Number(max)
    return undefined
  }, [params])

  const tagParams = useMemo(() => {
    const tag = params.get('tags')
    if (tag) {
      return tag.split(',').map((t) => Number(t))
    }
    return []
  }, [params])

  const handleSendClick = useCallback(() => {
    setParams((prev) => {
      prev.delete('page')
      // ランダムソートをリセット
      if (prev.get('order_by') === 'random') {
        prev.delete('order_by')
      }
      if (searchInput) {
        prev.set('title', searchInput)
      } else {
        prev.delete('title')
      }
      if (pageFilter[0]) {
        prev.set('min', pageFilter[0].toString())
      } else {
        prev.delete('min')
      }
      if (pageFilter[1]) {
        prev.set('max', pageFilter[1].toString())
      } else {
        prev.delete('max')
      }
      if (tagFilter.length > 0) {
        prev.set('tags', tagFilter.join(','))
      } else {
        prev.delete('tags')
      }
      // お気に入りフィルター
      if (favoriteFilter === 'favorite') {
        prev.set('favorite', 'true')
      } else if (favoriteFilter === 'not_favorite') {
        prev.set('favorite', 'false')
      } else {
        prev.delete('favorite')
      }
      // 読書状態フィルター（全選択時はパラメータを削除）
      if (readingStatusFilter.length === ALL_READING_STATUSES.length || readingStatusFilter.length === 0) {
        prev.delete('reading_status')
      } else {
        prev.set('reading_status', readingStatusFilter.join(','))
      }
      return prev
    })
    onClose?.()
  }, [setParams, onClose, searchInput, pageFilter, tagFilter, favoriteFilter, readingStatusFilter])

  const handleClearClick = useCallback(() => {
    setSearchInput('')
    setPageFilter([undefined, undefined])
    setTagFilter([])
    setFavoriteFilter('all')
    setReadingStatusFilter([...ALL_READING_STATUSES])
    setParams((prev) => {
      prev.delete('title')
      prev.delete('min')
      prev.delete('max')
      prev.delete('tags')
      prev.delete('page')
      prev.delete('favorite')
      prev.delete('reading_status')
      prev.delete('order_by')
      return prev
    })
    onClose?.()
  }, [setParams, onClose])

  const handleReadingStatusChange = useCallback((status: ReadingStatus, checked: boolean) => {
    setReadingStatusFilter((prev) => {
      if (checked) {
        return [...prev, status]
      }
      return prev.filter((s) => s !== status)
    })
  }, [])

  return (
    <div className="px-4">
      <div className="flex flex-col gap-3 py-2">
        <form
          onSubmit={(e) => {
            e.preventDefault()
            handleSendClick()
          }}
        >
          <SearchBox onChange={(e) => setSearchInput(e.target.value)} value={searchInput} />
        </form>
        {/* お気に入りフィルター */}
        <FavoriteSelector value={favoriteFilter} onChange={setFavoriteFilter} />
        {/* 読書状態フィルター */}
        <ReadingStatusSelector value={readingStatusFilter} onChange={handleReadingStatusChange} />
        <PageSelect onChange={setPageFilter} min={pageMin} max={pageMax} />
        {hasFullAccess && <TagSelector tags={domainTags} value={tagParams} onChange={setTagFilter} />}
      </div>
      <div className="sticky bottom-0 flex justify-between items-center gap-2 py-4 border-t border-white/10 bg-background">
        <Button variant="outline" className="w-full" onClick={handleClearClick}>
          クリア
        </Button>
        <Button className="w-full" onClick={handleSendClick}>
          検索
        </Button>
      </div>
    </div>
  )
}

// お気に入りフィルターコンポーネント（トグルボタングループ）
const FavoriteSelector = ({
  value,
  onChange,
}: {
  value: FavoriteFilter
  onChange: (value: FavoriteFilter) => void
}) => {
  return (
    <div className="flex justify-start items-center gap-2">
      <Badge>お気に入りの話</Badge>
      <div className="flex rounded-md overflow-hidden border border-white/20 w-fit">
        {FAVORITE_OPTIONS.map((option, index) => (
          <button
            key={option.value}
            type="button"
            onClick={() => onChange(option.value)}
            className={`
              px-3 py-1! text-xs! transition-colors
              ${index !== 0 ? 'border-l border-white/20' : ''}
              ${value === option.value ? 'bg-white text-black font-medium' : 'bg-black text-white hover:bg-white/10'}
            `}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  )
}

// 読書状態フィルターコンポーネント
const ReadingStatusSelector = ({
  value,
  onChange,
}: {
  value: ReadingStatus[]
  onChange: (status: ReadingStatus, checked: boolean) => void
}) => {
  return (
    <div className="flex flex-col gap-1">
      <Badge className="w-fit">読書状態</Badge>
      <div className="flex gap-4">
        {ALL_READING_STATUSES.map((status) => (
          <Label key={status} className="flex items-center gap-2 cursor-pointer">
            <Checkbox
              checked={value.includes(status)}
              onCheckedChange={(checked) => onChange(status, checked === true)}
            />
            <span className="text-sm">{READING_STATUS_LABELS[status]}</span>
          </Label>
        ))}
      </div>
    </div>
  )
}

const PageSelect = ({
  onChange,
  max,
  min,
}: {
  onChange: (value: (number | undefined)[]) => void
  max: number | undefined
  min: number | undefined
}) => {
  const [value, setValue] = useState<(number | undefined)[]>([min, max])

  return (
    <div className="flex justify-start items-center gap-2">
      <Badge>ページ数</Badge>
      <Input
        type="number"
        className="w-20 bg-black text-white"
        onChange={(e) => {
          const newVal = [e.target.value ? Number(e.target.value) : undefined, value[1]]
          onChange(newVal)
          setValue(newVal)
        }}
        value={value[0] ?? ''}
      />
      -
      <Input
        type="number"
        className="w-20 bg-black text-white"
        onChange={(e) => {
          const newVal = [value[0], e.target.value ? Number(e.target.value) : undefined]
          onChange(newVal)
          setValue(newVal)
        }}
        value={value[1] ?? ''}
      />
    </div>
  )
}

const TagSelector = ({
  onChange,
  tags,
  value,
}: {
  onChange: (value: number[]) => void
  tags: Tag[]
  value: number[]
}) => {
  const [selectedTags, setSelectedTags] = useState<{ type: string; id: number }[]>([])

  useEffect(() => {
    setSelectedTags(tags.filter((tag) => value.includes(tag.id)).map((tag) => ({ type: tag.type, id: tag.id })))
  }, [tags, value])

  const colorStyles: StylesConfig<OptionType, true> = {
    control: (styles) => ({
      ...styles,
      backgroundColor: 'black',
      color: 'white',
    }),
    multiValue: (styles) => ({
      ...styles,
      backgroundColor: '#333',
    }),
    menu: (styles) => ({
      ...styles,
      backgroundColor: 'black',
    }),
    // BottomSheet のスクロール領域でクリップされないよう body にポータルする
    menuPortal: (styles) => ({
      ...styles,
      zIndex: 10001,
    }),
    option: (styles) => ({
      ...styles,
      backgroundColor: 'black',
      color: 'white',
      ':hover': {
        ...styles[':hover'],
        backgroundColor: '#333',
      },
    }),
    multiValueLabel: (styles) => ({
      ...styles,
      color: 'white',
    }),
    multiValueRemove: (styles) => ({
      ...styles,
      color: 'white',
      ':hover': {
        backgroundColor: '#555',
        color: 'white',
      },
    }),
  }

  const sortedTags = useMemo(() => {
    if (!tags) return []
    const groupedTags = groupBy(tags, (o) => o.type)
    return sortBy(Object.entries(groupedTags), [(o) => o[0]]).map(([key, v]) => ({
      key,
      value: orderBy(v, ['name'], ['asc']).map((tag) => ({ label: tag.name, value: tag.id })),
    }))
  }, [tags])

  if (sortedTags.length === 0) return null

  return (
    <div className="flex flex-col gap-3">
      {sortedTags.map((tag) => {
        const selectedOptions = selectedTags
          .filter((t) => t.type === tag.key)
          .map((t) => ({ value: t.id, label: tag.value.find((v) => v.value === t.id)?.label || '' }))

        return (
          <div key={tag.key} className="flex flex-col gap-1">
            <Badge className="w-fit">{tag.key}</Badge>
            <Select<OptionType, true>
              isMulti
              options={tag.value}
              className="w-full"
              // 下に収まらない場合は上向きに開く（BottomSheet 下端での見切れ対策）
              menuPlacement="auto"
              menuPosition="fixed"
              menuPortalTarget={document.body}
              menuShouldScrollIntoView={false}
              maxMenuHeight={240}
              onChange={(newSelectedOptions: MultiValue<OptionType>) => {
                setSelectedTags((prev) => {
                  const newTags =
                    newSelectedOptions.length === 0
                      ? prev.filter((t) => t.type !== tag.key)
                      : [
                          ...prev.filter((t) => t.type !== tag.key),
                          ...newSelectedOptions.map((op: OptionType) => ({ type: tag.key, id: op.value })),
                        ]
                  onChange(newTags.map((t) => t.id))
                  return newTags
                })
              }}
              value={selectedOptions}
              styles={colorStyles}
            />
          </div>
        )
      })}
    </div>
  )
}
